const readGeminiPayload = (
    gemini
) => {

    return (
        gemini?.payload?.analysis ??
        gemini?.payload?.geminiAnalysis ??
        gemini?.payload?.result ??
        gemini?.payload ??
        gemini?.analysis ??
        gemini ??
        null
    );
};


const readNested = (
    source,
    paths
) => {

    for (
        const path
        of paths
    ) {

        const value =
            path.reduce(
                (
                    current,
                    key
                ) =>
                    current?.[
                        key
                    ],
                source
            );


        if (
            value !==
                undefined &&
            value !== null &&
            value !== ""
        ) {
            return value;
        }
    }


    return null;
};


const normalizeConfidence = (
    value
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }


    if (
        typeof value ===
            "number"
    ) {

        return Math.max(
            0,
            Math.min(
                100,
                value <= 1
                    ? value *
                      100
                    : value
            )
        );
    }


    const text =
        String(
            value
        )
            .trim()
            .toUpperCase();


    const numeric =
        Number(
            text.replace(
                "%",
                ""
            )
        );


    if (
        Number.isFinite(
            numeric
        )
    ) {

        return Math.max(
            0,
            Math.min(
                100,
                numeric <= 1
                    ? numeric *
                      100
                    : numeric
            )
        );
    }


    if (
        text === "HIGH"
    ) {
        return 85;
    }


    if (
        text === "MEDIUM" ||
        text === "MODERATE"
    ) {
        return 60;
    }


    if (
        text === "LOW"
    ) {
        return 30;
    }


    return null;
};


const normalizeStatus = (
    analysis
) => {

    const explicit =
        readNested(
            analysis,
            [
                [
                    "setup_assessment",
                    "status",
                ],

                [
                    "setup_assessment",
                    "decision",
                ],

                [
                    "setupAssessment",
                    "status",
                ],

                [
                    "setupAssessment",
                    "decision",
                ],

                [
                    "status",
                ],

                [
                    "decision",
                ],

                [
                    "approved",
                ],
            ]
        );


    if (
        typeof explicit ===
            "boolean"
    ) {
        return explicit
            ? "APPROVED"
            : "REJECTED";
    }


    if (
        explicit
    ) {

        const normalized =
            String(
                explicit
            )
                .trim()
                .toUpperCase();


        if (
            [
                "APPROVED",
                "ACCEPTED",
                "VALID",
                "YES",
            ].includes(
                normalized
            )
        ) {
            return "APPROVED";
        }


        if (
            [
                "REJECTED",
                "REJECT",
                "INVALID",
                "NO",
            ].includes(
                normalized
            )
        ) {
            return "REJECTED";
        }
    }


    return "OBSERVATIONAL";
};


const normalizeBias = (
    analysis
) => {

    const value =
        readNested(
            analysis,
            [
                [
                    "setup_assessment",
                    "direction",
                ],

                [
                    "setup_assessment",
                    "bias",
                ],

                [
                    "setupAssessment",
                    "direction",
                ],

                [
                    "setupAssessment",
                    "bias",
                ],

                [
                    "market_context",
                    "direction",
                ],

                [
                    "market_context",
                    "bias",
                ],

                [
                    "marketContext",
                    "direction",
                ],

                [
                    "marketContext",
                    "bias",
                ],

                [
                    "direction",
                ],

                [
                    "bias",
                ],
            ]
        );


    if (
        !value
    ) {
        return "NEUTRAL";
    }


    const normalized =
        String(
            value
        )
            .trim()
            .toUpperCase();


    if (
        normalized.includes(
            "LONG"
        ) ||
        normalized.includes(
            "BULL"
        )
    ) {
        return "LONG";
    }


    if (
        normalized.includes(
            "SHORT"
        ) ||
        normalized.includes(
            "BEAR"
        )
    ) {
        return "SHORT";
    }


    return "NEUTRAL";
};


const readText = (
    analysis,
    paths,
    fallback
) => {

    const value =
        readNested(
            analysis,
            paths
        );


    if (
        value === null
    ) {
        return fallback;
    }


    if (
        typeof value ===
            "string"
    ) {
        return value;
    }


    return JSON.stringify(
        value,
        null,
        2
    );
};


const readList = (
    analysis,
    paths
) => {

    const value =
        readNested(
            analysis,
            paths
        );


    if (
        Array.isArray(
            value
        )
    ) {
        return value;
    }


    if (
        typeof value ===
            "string"
    ) {
        return [
            value,
        ];
    }


    return [];
};


export function GeminiAnalystPanel({
    gemini,
}) {

    if (
        !gemini
    ) {

        return (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-xl shadow-black/10">

                <div className="mb-4 flex items-center justify-between">

                    <div>

                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Gemini Analyst
                        </p>

                        <p className="mt-1 text-sm text-slate-400">
                            Waiting for deterministic setup analysis
                        </p>

                    </div>


                    <span className="rounded-full bg-slate-800 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Analyst Only
                    </span>

                </div>


                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-500">
                    No Gemini analysis received yet.
                </div>

            </section>
        );
    }


    const analysis =
        readGeminiPayload(
            gemini
        );


    const confidence =
        normalizeConfidence(
            readNested(
                analysis,
                [
                    [
                        "analyst_confidence",
                    ],

                    [
                        "analystConfidence",
                    ],

                    [
                        "confidence",
                    ],

                    [
                        "confidence_score",
                    ],

                    [
                        "confidenceScore",
                    ],
                ]
            )
        );


    const status =
        normalizeStatus(
            analysis
        );


    const bias =
        normalizeBias(
            analysis
        );


    const summary =
        readText(
            analysis,
            [
                [
                    "summary",
                ],
            ],
            "No summary supplied."
        );


    const marketContext =
        readText(
            analysis,
            [
                [
                    "market_context",
                ],

                [
                    "marketContext",
                ],
            ],
            "No market context supplied."
        );


    const smcReasoning =
        readText(
            analysis,
            [
                [
                    "smc_reasoning",
                ],

                [
                    "smcReasoning",
                ],

                [
                    "setup_assessment",
                    "rationale",
                ],

                [
                    "setupAssessment",
                    "rationale",
                ],
            ],
            "No SMC rationale supplied."
        );


    const lifecycleCommentary =
        readText(
            analysis,
            [
                [
                    "lifecycle_commentary",
                ],

                [
                    "lifecycleCommentary",
                ],
            ],
            "No lifecycle commentary supplied."
        );


    const keyRisks =
        readList(
            analysis,
            [
                [
                    "key_risks",
                ],

                [
                    "keyRisks",
                ],
            ]
        );


    const invalidatingConditions =
        readList(
            analysis,
            [
                [
                    "invalidating_conditions",
                ],

                [
                    "invalidatingConditions",
                ],
            ]
        );


    const eventTimestamp =
        gemini?.timestamp ??
        gemini?.payload?.timestamp ??
        null;


    return (
        <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-xl shadow-black/10">

            <div className="mb-4 flex items-center justify-between">

                <div>

                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Gemini Analyst
                    </p>

                    <p className="mt-1 text-sm text-slate-400">
                        Live observational analysis
                    </p>

                </div>


                <span className="rounded-full bg-indigo-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-300">
                    ANALYST ONLY
                </span>

            </div>


            <div className="grid grid-cols-3 gap-2">

                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                    <p className="text-[10px] uppercase tracking-wide text-slate-600">
                        Confidence
                    </p>

                    <p className="mt-1 text-lg font-bold text-slate-100">
                        {confidence === null
                            ? "—"
                            : `${confidence.toFixed(
                                  0
                              )}%`}
                    </p>

                </div>


                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                    <p className="text-[10px] uppercase tracking-wide text-slate-600">
                        Status
                    </p>

                    <p
                        className={[
                            "mt-1 text-sm font-bold",
                            status ===
                                "APPROVED"
                                ? "text-emerald-400"
                                : status ===
                                  "REJECTED"
                                ? "text-red-400"
                                : "text-amber-400",
                        ].join(" ")}
                    >
                        {status}
                    </p>

                </div>


                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                    <p className="text-[10px] uppercase tracking-wide text-slate-600">
                        Bias
                    </p>

                    <p
                        className={[
                            "mt-1 text-sm font-bold",
                            bias ===
                                "LONG"
                                ? "text-emerald-400"
                                : bias ===
                                  "SHORT"
                                ? "text-red-400"
                                : "text-slate-400",
                        ].join(" ")}
                    >
                        {bias}
                    </p>

                </div>

            </div>


            <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Setup Rationale
                </p>

                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-300">
                    {summary}
                </p>

            </div>


            <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    SMC Structural Notes
                </p>

                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-300">
                    {smcReasoning}
                </p>

            </div>


            <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Market Context
                </p>

                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-400">
                    {marketContext}
                </p>

            </div>


            {keyRisks.length >
                0 && (
                <div className="mt-3 rounded-xl border border-amber-900/40 bg-amber-950/10 p-3">

                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-500">
                        Key Risks
                    </p>

                    <ul className="space-y-1 text-sm text-slate-400">

                        {keyRisks.map(
                            (
                                risk,
                                index
                            ) => (
                                <li
                                    key={`${index}-${String(
                                        risk
                                    )}`}
                                    className="flex gap-2"
                                >
                                    <span className="text-amber-500">
                                        •
                                    </span>

                                    <span>
                                        {typeof risk ===
                                        "string"
                                            ? risk
                                            : JSON.stringify(
                                                  risk
                                              )}
                                    </span>
                                </li>
                            )
                        )}

                    </ul>

                </div>
            )}


            {invalidatingConditions.length >
                0 && (
                <div className="mt-3 rounded-xl border border-red-900/40 bg-red-950/10 p-3">

                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-red-400">
                        Invalidating Conditions
                    </p>

                    <ul className="space-y-1 text-sm text-slate-400">

                        {invalidatingConditions.map(
                            (
                                condition,
                                index
                            ) => (
                                <li
                                    key={`${index}-${String(
                                        condition
                                    )}`}
                                    className="flex gap-2"
                                >
                                    <span className="text-red-500">
                                        •
                                    </span>

                                    <span>
                                        {typeof condition ===
                                        "string"
                                            ? condition
                                            : JSON.stringify(
                                                  condition
                                              )}
                                    </span>
                                </li>
                            )
                        )}

                    </ul>

                </div>
            )}


            <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">

                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Lifecycle Commentary
                </p>

                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-400">
                    {lifecycleCommentary}
                </p>

            </div>


            {eventTimestamp && (
                <p className="mt-3 text-[10px] text-slate-600">
                    Updated{" "}
                    {new Date(
                        eventTimestamp
                    ).toLocaleString()}
                </p>
            )}

        </section>
    );
}


export default GeminiAnalystPanel;